/**
 * GapQuestion MDXEditor Plugin
 *
 * Renders a read-only question/title that cannot be edited or deleted.
 * Users answer below the question in normal paragraph text.
 *
 * MARKDOWN SYNTAX (leafDirective):
 * ::GapQuestion[What are your goals for this session?]
 */

import { DirectiveDescriptor, realmPlugin, createRootEditorSubscription$, $isDirectiveNode } from '@mdxeditor/editor'
import type { LeafDirective } from 'mdast-util-directive'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import { KEY_BACKSPACE_COMMAND, KEY_DELETE_COMMAND, DELETE_CHARACTER_COMMAND, COMMAND_PRIORITY_CRITICAL, $getSelection, $isRangeSelection, $isNodeSelection, LexicalEditor, LexicalNode, $getRoot } from 'lexical'

const DIRECTIVE_NAME = 'GapQuestion'

interface GapQuestionProps {
   mdastNode: LeafDirective
}

function GapQuestionComponent({ mdastNode }: GapQuestionProps) {
   const questionText = mdastNode.children?.[0]?.type === 'text'
      ? mdastNode.children[0].value
      : ''

   return (
      <Box
         contentEditable={false}
         data-gap-question="true"
         sx={{
            userSelect: 'none',
         }}
      >
         <Typography variant="body1Bold">
            {questionText}
         </Typography>
      </Box>
   )
}

export const GapQuestionDirectiveDescriptor: DirectiveDescriptor<LeafDirective> = {
   name: DIRECTIVE_NAME,
   type: 'leafDirective',
   testNode(node) {
      return node.name === DIRECTIVE_NAME
   },
   attributes: [],
   hasChildren: true,
   Editor: GapQuestionComponent,
}

/**
 * Plugin that prevents deletion of GapQuestion directives
 */
export const gapQuestionProtectionPlugin = realmPlugin({
   init(realm) {
      realm.pub(createRootEditorSubscription$, (editor: LexicalEditor) => {
         const unregisterBackspace = editor.registerCommand(
            KEY_BACKSPACE_COMMAND,
            (event: KeyboardEvent) => {
               const shouldBlock = shouldBlockDeletion('backspace')
               if (shouldBlock) {
                  event.preventDefault()
               }
               return shouldBlock
            },
            COMMAND_PRIORITY_CRITICAL
         )

         const unregisterDelete = editor.registerCommand(
            KEY_DELETE_COMMAND,
            (event: KeyboardEvent) => {
               const shouldBlock = shouldBlockDeletion('delete')
               if (shouldBlock) {
                  event.preventDefault()
               }
               return shouldBlock
            },
            COMMAND_PRIORITY_CRITICAL
         )

         const unregisterDeleteChar = editor.registerCommand(
            DELETE_CHARACTER_COMMAND,
            (isBackward: boolean) => {
               const shouldBlock = shouldBlockDeletion(isBackward ? 'backspace' : 'delete')
               return shouldBlock
            },
            COMMAND_PRIORITY_CRITICAL
         )

         return () => {
            unregisterBackspace()
            unregisterDelete()
            unregisterDeleteChar()
         }
      })
   },
})

function isQuestionNode(node: LexicalNode | null | undefined): boolean {
   if (!node) return false
   if ($isDirectiveNode(node)) {
      const mdastNode = (node as any).getMdastNode?.()
      return mdastNode?.name === DIRECTIVE_NAME
   }
   return false
}

function getParentParagraphSibling(node: LexicalNode): LexicalNode | null {
   // Walk up to find the paragraph/block level, then get its previous sibling
   let current: LexicalNode | null = node
   while (current) {
      const parent: LexicalNode | null = current.getParent()
      if (parent && parent === $getRoot()) {
         return current.getPreviousSibling()
      }
      current = parent
   }
   return null
}

function getNextBlockSibling(node: LexicalNode): LexicalNode | null {
   // Walk up to find the paragraph/block level, then get its next sibling
   let current: LexicalNode | null = node
   while (current) {
      const parent: LexicalNode | null = current.getParent()
      if (parent && parent === $getRoot()) {
         return current.getNextSibling()
      }
      current = parent
   }
   return null
}

function shouldBlockDeletion(direction: 'backspace' | 'delete'): boolean {
   const selection = $getSelection()

   // Handle node selection (when directive is directly selected)
   if ($isNodeSelection(selection)) {
      const nodes = selection.getNodes()
      for (const node of nodes) {
         if (isQuestionNode(node)) {
            return true
         }
      }
   }

   // Handle range selection (cursor in text)
   if ($isRangeSelection(selection)) {
      const anchorNode = selection.anchor.getNode()

      // Get ALL nodes in the selection range
      const selectedNodes = selection.getNodes()

      // Check if any selected node is a question or contains a question
      for (const node of selectedNodes) {
         if (isQuestionNode(node)) {
            return true
         }
         // Also check siblings at the root level - the directive might be between selected nodes
         const parent = node.getParent()
         if (parent && parent === $getRoot()) {
            // Check all siblings between selection start and end
            const siblings = parent.getChildren()
            for (const sibling of siblings) {
               if (isQuestionNode(sibling)) {
                  // If we're selecting across nodes and directive is at root level, protect it
                  if (selectedNodes.length > 1 || !selection.isCollapsed()) {
                     return true
                  }
               }
            }
         }
      }

      // BACKSPACE only: Check if at the very start of a paragraph that follows a question
      // This prevents backspace from deleting the question when cursor is at start of answer
      if (direction === 'backspace' && selection.anchor.offset === 0 && selection.isCollapsed()) {
         const prevSibling = getParentParagraphSibling(anchorNode)
         if (isQuestionNode(prevSibling)) {
            return true
         }
      }

      // DELETE only: Check if at the very end of a paragraph that precedes a question
      // This prevents DELETE key from deleting the question when cursor is at end before it
      if (direction === 'delete' && selection.isCollapsed()) {
         const textContent = anchorNode.getTextContent()
         if (selection.anchor.offset === textContent.length) {
            const nextSibling = getNextBlockSibling(anchorNode)
            if (isQuestionNode(nextSibling)) {
               return true
            }
         }
      }
   }

   return false
}
